/*!
 * PabloDots — Slider Vanilla para "Best Products"
 * Autor: Pablo + ChatGPT
 * Propósito: mover la lógica del slider/dots fuera del Liquid.
 * Seguro para múltiples instancias en la misma página y Theme Editor.
 */
(function (w, d) {
  'use strict';

  // Estado por raíz (sección)
  const instances = new Map();
  let globalHandlersInstalled = false;

  // Utilidades
  const toInt = (v, def) => {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? def : n;
  };
  const vw = () => Math.max(d.documentElement.clientWidth || 0, w.innerWidth || 0);
  const debounce = (fn, ms) => {
    let t;
    return function () {
      clearTimeout(t);
      const self = this, args = arguments;
      t = setTimeout(() => fn.apply(self, args), ms);
    };
  };

  // Breakpoints (deben coincidir con los CSS)
  const BP_WIDE = 1300;
  const BP_DESK = 992;
  const BP_TABLET = 855;
  const BP_XS = 591;

  // Obtiene # de columnas según viewport + data-attrs
  function colsNow(root) {
    const wpx = Math.max(d.documentElement.clientWidth || 0, w.innerWidth || 0);
    const colsXS = toInt(root.getAttribute('data-cols-mobile-xs'), 2);     // <=590
    const colsM  = toInt(root.getAttribute('data-cols-mobile-small'), 3);  // 591–854
    const colsT  = toInt(root.getAttribute('data-cols-tablet'), 4);        // 855–991
    const colsD  = toInt(root.getAttribute('data-cols-desktop'), 5);       // 992–1299
    const colsW  = toInt(root.getAttribute('data-cols-wide-desktop'), 6);  // >=1300

    if (wpx >= BP_WIDE)   return Math.max(colsW, 1);
    if (wpx >= BP_DESK)   return Math.max(colsD, 1);
    if (wpx >= BP_TABLET) return Math.max(colsT, 1);
    if (wpx >= BP_XS)     return Math.max(colsM, 1);
    return Math.max(colsXS, 1);
  }

  function applyCols(listEl, root) {
    if (!listEl || !root) return;
    const c = colsNow(root);
    listEl.style.setProperty('--cols', String(c));
  }

  function getStep(listEl) {
    if (!listEl || !listEl.firstElementChild) return 0;
    const first = listEl.firstElementChild;
    const rect = first.getBoundingClientRect();
    const gap = parseFloat(getComputedStyle(listEl).gap || '12') || 0;
    return rect.width + gap;
  }

  function clamp(val, min, max) { return Math.min(Math.max(val, min), max); }

  function pagesNow(listEl, root) {
    if (!listEl || !root) return 1;
    const styles = getComputedStyle(listEl);
    const cols = parseInt(styles.getPropertyValue('--cols')) || colsNow(root);
    const total = listEl.children.length;
    return Math.max(total - cols + 1, 1);
  }

  function updateControls(state) {
    const { listEl, prevBtn, nextBtn, root } = state;
    if (!listEl || !prevBtn || !nextBtn || !root) return;
    const styles = getComputedStyle(listEl);
    const cols = parseInt(styles.getPropertyValue('--cols')) || colsNow(root);
    const total = listEl.children.length;
    const controlsEnabled = total > cols;
    prevBtn.disabled = !controlsEnabled;
    nextBtn.disabled = !controlsEnabled;
  }

  function buildDots(state) {
    const { listEl, dotsEl, root } = state;
    if (!listEl || !dotsEl || !root) return;

    const pages = pagesNow(listEl, root);
    if (pages <= 1) {
      dotsEl.hidden = true;
      dotsEl.innerHTML = '';
      return;
    }

    let html = '';
    for (let i = 0; i < pages; i++) {
      html += `<button type="button" class="bp-dot" data-idx="${i}" aria-label="Ir a página ${i + 1}"></button>`;
    }
    dotsEl.innerHTML = html;
    dotsEl.hidden = false;

    // Delegación click
    dotsEl.onclick = (e) => {
      const t = e.target;
      if (t && t.matches('.bp-dot')) {
        const idx = parseInt(t.getAttribute('data-idx'), 10) || 0;
        scrollToIndex(state, idx); // smooth
      }
    };
    updateDots(state);
  }

  function updateDots(state) {
    const { dotsEl, listEl, root } = state;
    if (!dotsEl || !listEl || !root) return;

    const pages = pagesNow(listEl, root);
    const dots = dotsEl.querySelectorAll('.bp-dot');

    // Si cambió el # de páginas, reconstruir
    if (dots.length !== pages) { buildDots(state); return; }

    dots.forEach((d) => d.removeAttribute('aria-current'));

    const step = getStep(listEl);
    const idxFromScroll = step > 0 ? Math.round(listEl.scrollLeft / step) : (state.currentIndex || 0);
    state.currentIndex = Math.min(Math.max(idxFromScroll, 0), pages - 1);

    const cur = dotsEl.querySelector(`.bp-dot[data-idx="${state.currentIndex}"]`);
    if (cur) cur.setAttribute('aria-current', 'true');
  }

  // ✅ ahora permite elegir behavior (por defecto smooth)
  function scrollToIndex(state, idx, behavior) {
    const { listEl, root } = state;
    if (!listEl || !root) return;
    const step = getStep(listEl);
    if (step <= 0) return;

    const maxIdx = Math.max(listEl.children.length - 1, 0);
    const nextIdx = Math.min(Math.max(idx, 0), maxIdx);
    state.currentIndex = nextIdx;

    listEl.scrollTo({
      left: step * state.currentIndex,
      behavior: behavior || 'smooth'
    });

    updateControls(state);
    updateDots(state);
  }

  function next(state) {
    const { listEl, root } = state;
    if (!listEl || !root) return;
    const cols = parseInt(getComputedStyle(listEl).getPropertyValue('--cols')) || colsNow(root);
    const maxFirstIndex = Math.max(listEl.children.length - cols, 0);
    state.currentIndex = (state.currentIndex >= maxFirstIndex) ? 0 : state.currentIndex + 1;
    scrollToIndex(state, state.currentIndex); // smooth
  }

  function prev(state) {
    const { listEl, root } = state;
    if (!listEl || !root) return;
    const cols = parseInt(getComputedStyle(listEl).getPropertyValue('--cols')) || colsNow(root);
    const maxFirstIndex = Math.max(listEl.children.length - cols, 0);
    state.currentIndex = (state.currentIndex <= 0) ? maxFirstIndex : state.currentIndex - 1;
    scrollToIndex(state, state.currentIndex); // smooth
  }

  function onResize(state) {
    const { listEl, root } = state;
    if (!listEl || !root) return;

    // ✅ FIX #1: ignorar "resize" si NO cambió el ancho (barra móvil)
    const wNow = vw();
    if (typeof state._lastW === 'number' && Math.abs(wNow - state._lastW) < 2) return;
    state._lastW = wNow;

    // ✅ mantener el índice actual (antes de recalcular)
    const stepBefore = getStep(listEl);
    const pagesBefore = pagesNow(listEl, root);
    const idxFromScroll = stepBefore > 0 ? Math.round(listEl.scrollLeft / stepBefore) : (state.currentIndex || 0);
    state.currentIndex = clamp(idxFromScroll, 0, pagesBefore - 1);

    // Reflow normal
    applyCols(listEl, root);
    updateControls(state);
    buildDots(state);

    // ✅ FIX #2: NO reset a 0; reposicionar sin animación
    scrollToIndex(state, state.currentIndex, 'auto');
  }

  // Inicializa una raíz concreta (div[data-pablodots="best-products"])
  function initRoot(root) {
    if (!root || instances.has(root)) return;

    const listEl  = root.querySelector('.bp-list');
    const dotsEl  = root.querySelector('.bp-dots');
    const prevBtn = root.querySelector('[id^="BPPrev-"]');
    const nextBtn = root.querySelector('[id^="BPNext-"]');

    if (!listEl || !prevBtn || !nextBtn) return;

    const state = {
      root,
      listEl,
      dotsEl,
      prevBtn,
      nextBtn,
      currentIndex: 0,
      _handlers: {},
      _lastW: vw() // ✅ guarda ancho inicial
    };

    // Aplicar columnas inicial
    applyCols(listEl, root);

    // Listeners
    state._handlers.scroll = debounce(() => updateDots(state), 50);
    state._handlers.prev = () => prev(state);
    state._handlers.next = () => next(state);
    state._handlers.resize = debounce(() => onResize(state), 120);

    listEl.addEventListener('scroll', state._handlers.scroll);
    prevBtn.addEventListener('click', state._handlers.prev);
    nextBtn.addEventListener('click', state._handlers.next);
    w.addEventListener('resize', state._handlers.resize);

    // Construcción inicial
    updateControls(state);
    buildDots(state);
    scrollToIndex(state, 0, 'auto');

    instances.set(root, state);
  }

  // Desmonta una raíz (para Theme Editor/unload)
  function destroyRoot(root) {
    const state = instances.get(root);
    if (!state) return;

    const { listEl, prevBtn, nextBtn, _handlers } = state;
    if (listEl && _handlers.scroll) listEl.removeEventListener('scroll', _handlers.scroll);
    if (prevBtn && _handlers.prev) prevBtn.removeEventListener('click', _handlers.prev);
    if (nextBtn && _handlers.next) nextBtn.removeEventListener('click', _handlers.next);
    if (_handlers.resize) w.removeEventListener('resize', _handlers.resize);

    instances.delete(root);
  }

  // Auto-inicialización
  function initAll() {
    const roots = d.querySelectorAll('[data-pablodots="best-products"]');
    roots.forEach(initRoot);
  }

  // Observa removals por si el editor elimina nodos sin disparar eventos
  const mo = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      m.removedNodes && m.removedNodes.forEach((node) => {
        if (node.nodeType === 1 && instances.has(node)) {
          destroyRoot(node);
        } else if (node.nodeType === 1) {
          const removedRoots = node.querySelectorAll ? node.querySelectorAll('[data-pablodots="best-products"]') : [];
          removedRoots.forEach(destroyRoot);
        }
      });
    });
  });

  function installGlobalHandlersOnce() {
    if (globalHandlersInstalled) return;
    globalHandlersInstalled = true;

    // DOM listo
    if (d.readyState === 'loading') {
      d.addEventListener('DOMContentLoaded', initAll, { once: true });
    } else {
      initAll();
    }

    // Theme Editor de Shopify
    d.addEventListener('shopify:section:load', (e) => {
      const section = d.getElementById('shopify-section-' + (e && e.detail && e.detail.sectionId));
      if (!section) return;
      const roots = section.querySelectorAll('[data-pablodots="best-products"]');
      roots.forEach(initRoot);
    });

    d.addEventListener('shopify:section:unload', (e) => {
      const section = d.getElementById('shopify-section-' + (e && e.detail && e.detail.sectionId));
      if (!section) return;
      const roots = section.querySelectorAll('[data-pablodots="best-products"]');
      roots.forEach(destroyRoot);
    });

    // MutationObserver para desmontes no anunciados
    mo.observe(d.documentElement || d.body, { childList: true, subtree: true });
  }

  // Exponer API mínima (opcional)
  w.PabloDots = w.PabloDots || {
    init(selectorOrEl) {
      const el = typeof selectorOrEl === 'string' ? d.querySelector(selectorOrEl) : selectorOrEl;
      if (el) initRoot(el);
    },
    destroy(selectorOrEl) {
      const el = typeof selectorOrEl === 'string' ? d.querySelector(selectorOrEl) : selectorOrEl;
      if (el) destroyRoot(el);
    },
    reflow(selectorOrEl) {
      const el = typeof selectorOrEl === 'string' ? d.querySelector(selectorOrEl) : selectorOrEl;
      const state = instances.get(el);
      if (state) {
        applyCols(state.listEl, state.root);
        updateControls(state);
        buildDots(state);
      }
    }
  };

  installGlobalHandlersOnce();
})(window, document);
